/*
 * @Author: Ryan
 * @Date: 2026-02-04
 * @Description: Features section with alternating layout for Face Swap SEO page
 */
import Image from 'next/image';
import React from 'react';

import StartButton from './StartButton';

interface FeatureItemProps {
  title: string;
  description: string;
  ctaText: string;
  imageUrl: string;
  imageAlt: string;
  reverse?: boolean;
}

const FeatureItem: React.FC<FeatureItemProps> = ({
  title,
  description,
  ctaText,
  imageUrl,
  imageAlt,
  reverse = false,
}) => {
  return (
    <div
      className={`flex flex-col ${reverse ? 'sm:flex-row-reverse' : 'sm:flex-row'} items-center gap-6 pad:gap-8 sm:gap-12`}
    >
      {/* Image */}
      <div className="flex-1 w-full">
        <div className="rounded-2xl overflow-hidden">
          <Image
            src={imageUrl}
            alt={imageAlt}
            width={540}
            height={360}
            className="w-full h-auto object-cover"
            loading="lazy"
          />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 w-full flex flex-col items-start">
        <h3 className="text-2xl pad:text-3xl sm:text-4xl font-bold text-gray-900 mb-3 pad:mb-4">
          {title}
        </h3>
        <p className="text-sm pad:text-base sm:text-base text-gray-600 mb-4 pad:mb-6 leading-relaxed">
          {description}
        </p>
        <StartButton text={ctaText} />
      </div>
    </div>
  );
};

const ASSET_PREFIX = 'https://assets.falcocut.ai/face-swap/';

const features = [
  {
    title: 'Swift & Seamless Face Swaps Online',
    description:
      'Experience instant face replacement powered by AI. Upload your video, and within moments see a flawless swap that blends naturally with lighting, angles, and expressions. No need for complicated tools or editing skills — the process is automatic, accurate, and fast. Whether you want to enhance portraits, experiment with creative edits, or generate quick content for social media, our technology ensures smooth results every time. Face swapping has never been this effortless and reliable.',
    ctaText: 'Swap Your Face',
    imageUrl: `${ASSET_PREFIX}swift-and-seamless-face-swaps-online.webp`,
    imageAlt: 'Swift and seamless AI face swap online',
  },
  {
    title: 'AI Face Swap for Videos',
    description:
      "Our advanced AI delivers studio-quality face swaps for videos, preserving natural lighting, angles, and facial expressions. Whether you're a creator making memes, a marketer building campaigns, or a filmmaker testing casting options, you can generate professional-grade content in minutes. Upload any video, and let the system replace faces seamlessly with hyper-realistic detail. Outputs are available up to 4K resolution, ensuring your projects look polished across social platforms, marketing sites, or client presentations.",
    ctaText: 'Swap Your Face',
    imageUrl: `${ASSET_PREFIX}ai-face-swap-for-videos.webp`,
    imageAlt: 'AI face swap for videos',
  },
  {
    title: 'Multiple Face Swap for Groups and Scenes',
    description:
      'Not just single portraits—our AI can detect and swap multiple faces in group or crowded videos. Whether you want to edit a family video, a classroom video, or a business team presentation, the system ensures each face is consistently mapped and replaced across every frame. Multi-face support makes it easy to create funny group memes, polished business materials, or entertainment clips with multiple actors swapped at once.',
    ctaText: 'Swap Your Face',
    imageUrl: `${ASSET_PREFIX}multiple-face-swap-for-groups-and-scenes.webp`,
    imageAlt: 'Multiple face swap for groups and scenes',
  },
  {
    title: 'Funny Face Swap for Memes and Reactions',
    description:
      'Bring fun and personality to your videos with meme-style and reaction-based face swaps. Replace faces in short video clips to create humorous reactions, playful moments, or entertaining content designed to make people laugh. Simply upload a video and select a reference face, and FalcoCut will seamlessly blend facial expressions, head movement, and lighting for natural-looking results. No manual editing required—everything is handled automatically by AI.',
    ctaText: 'Swap Your Face',
    imageUrl: `${ASSET_PREFIX}gif-face-swap-for-memes-and-reactions.webp`,
    imageAlt: 'Funny face swap for memes and reactions',
  },
  {
    title: 'Gender Face Swap for Creative Transformations',
    description:
      'Explore a different version of yourself with realistic gender face swapping. Our AI reimagines your look as the opposite gender while keeping key facial features intact for a natural transformation. Perfect for TikTok trends, Instagram challenges, or personal curiosity, gender swap results are engaging, fun, and highly shareable. Many creators also use it for experimental character design or to craft playful marketing content.',
    ctaText: 'Swap Your Face',
    imageUrl: `${ASSET_PREFIX}gender-face-swap-for-creative-transformations.webp`,
    imageAlt: 'Gender face swap for creative transformations',
  },
  {
    title: 'Secure & Professional-Grade Platform',
    description:
      "Every upload is encrypted, and files are deleted automatically after processing, so your data remains private. Runs entirely online—no downloads or software required. Whether you're swapping faces for fun, for business campaigns, or for professional content production, our platform ensures fast performance, commercial-use rights, and output ready for social media, ads, and even film projects.",
    ctaText: 'Swap Your Face',
    imageUrl: `${ASSET_PREFIX}secure-and-professional-grade-platform.webp`,
    imageAlt: 'Secure and professional-grade face swap platform',
  },
];

const FeaturesSection: React.FC = () => {
  return (
    <section className="w-full bg-white py-[90px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        <h2 className="text-2xl pad:text-3xl sm:text-4xl font-bold text-center text-gray-900 mb-3 pad:mb-4">
          Powerful AI Face Swap Features
        </h2>

        <p className="text-sm pad:text-base text-center text-gray-600 max-w-4xl mx-auto mb-6 pad:mb-8">
          Discover everything you can do with our AI-powered face swap tool. From
          ultra-realistic video swaps and multi-face replacement to playful memes and
          creative transformations, our features are designed for both fun and
          professional use.
        </p>

        <div className="space-y-12 pad:space-y-16 sm:space-y-24">
          {features.map((feature, index) => (
            <FeatureItem
              key={index}
              title={feature.title}
              description={feature.description}
              ctaText={feature.ctaText}
              imageUrl={feature.imageUrl}
              imageAlt={feature.imageAlt}
              reverse={index % 2 === 1}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
